import Dexie, { type EntityTable } from 'dexie'
import type {
  AjustePedido, Alerta, AliquotaIcms, Compromisso, AliquotaSt, Cliente, ClienteRepresentada,
  ComissaoPercentual, CondicaoPagamento, ConfiguracaoMetas, CursorSync, DescontoFrete,
  EntradaOutbox, Importacao, ItemPedido, LancamentoComissao, DiferencaComissao, Meta,
  Empresa, Pedido, PerfilImportacao, Preco, PrecoNegociado, Produto, RegraComissao,
  Representada, Segmento, SubPedido, TabelaPreco, Visita,
} from './tipos'

// Ordem de pull: pais antes de filhos (FKs locais sao apenas logicas, mas
// a ordem evita telas com referencias ausentes durante a primeira carga).
export const TABELAS_SYNC = [
  'representadas',
  'empresas',
  'condicoes_pagamento',
  'segmentos',
  'clientes',
  'tabelas_preco',
  'clientes_representadas',
  'produtos',
  'aliquotas_icms',
  'aliquotas_st',
  'descontos_frete',
  'precos',
  'precos_negociados',
  'pedidos',
  'sub_pedidos',
  'itens_pedido',
  'ajustes_pedido',
  'visitas',
  'compromissos',
  'metas',
  'configuracao_metas',
  'regras_comissao',
  'comissao_percentuais',
  'lancamentos_comissao',
  'diferencas_comissao',
  'perfis_importacao',
  'importacoes',
  'alertas',
] as const

export type TabelaSync = (typeof TABELAS_SYNC)[number]

export const banco = new Dexie('crm-multimarca') as Dexie & {
  representadas: EntityTable<Representada, 'id'>
  empresas: EntityTable<Empresa, 'id'>
  condicoes_pagamento: EntityTable<CondicaoPagamento, 'id'>
  clientes: EntityTable<Cliente, 'id'>
  clientes_representadas: EntityTable<ClienteRepresentada, 'id'>
  produtos: EntityTable<Produto, 'id'>
  aliquotas_icms: EntityTable<AliquotaIcms, 'id'>
  aliquotas_st: EntityTable<AliquotaSt, 'id'>
  descontos_frete: EntityTable<DescontoFrete, 'id'>
  tabelas_preco: EntityTable<TabelaPreco, 'id'>
  precos: EntityTable<Preco, 'id'>
  precos_negociados: EntityTable<PrecoNegociado, 'id'>
  pedidos: EntityTable<Pedido, 'id'>
  sub_pedidos: EntityTable<SubPedido, 'id'>
  itens_pedido: EntityTable<ItemPedido, 'id'>
  ajustes_pedido: EntityTable<AjustePedido, 'id'>
  visitas: EntityTable<Visita, 'id'>
  compromissos: EntityTable<Compromisso, 'id'>
  segmentos: EntityTable<Segmento, 'id'>
  metas: EntityTable<Meta, 'id'>
  configuracao_metas: EntityTable<ConfiguracaoMetas, 'id'>
  regras_comissao: EntityTable<RegraComissao, 'id'>
  comissao_percentuais: EntityTable<ComissaoPercentual, 'id'>
  lancamentos_comissao: EntityTable<LancamentoComissao, 'id'>
  diferencas_comissao: EntityTable<DiferencaComissao, 'id'>
  perfis_importacao: EntityTable<PerfilImportacao, 'id'>
  importacoes: EntityTable<Importacao, 'id'>
  alertas: EntityTable<Alerta, 'id'>
  outbox: EntityTable<EntradaOutbox, 'id'>
  cursores_sync: EntityTable<CursorSync, 'tabela'>
}

banco.version(1).stores({
  representadas: 'id, nome',
  condicoes_pagamento: 'id, representada_id',
  clientes: 'id, razao_social, municipio, regiao, dia_visita, status',
  clientes_representadas: 'id, cliente_id, representada_id, [cliente_id+representada_id]',
  produtos: 'id, representada_id, codigo, [representada_id+codigo], descricao',
  tabelas_preco: 'id, representada_id',
  precos: 'id, tabela_preco_id, produto_id, [tabela_preco_id+produto_id]',
  precos_negociados: 'id, cliente_id, produto_id, [cliente_id+produto_id]',
  pedidos: 'id, cliente_id, numero, data, status',
  sub_pedidos: 'id, pedido_id, representada_id, status',
  itens_pedido: 'id, sub_pedido_id, produto_id',
  ajustes_pedido: 'id, sub_pedido_id, motivo',
  visitas: 'id, cliente_id, data_hora',
  metas: 'id, tipo, periodo_inicio',
  regras_comissao: 'id, representada_id',
  comissao_percentuais: 'id, regra_id',
  lancamentos_comissao: 'id, sub_pedido_id, status',
  perfis_importacao: 'id, representada_id, tipo',
  importacoes: 'id, status',
  alertas: 'id, cliente_id, tipo',
  outbox: 'id, criado_em',
  cursores_sync: 'tabela',
})

// v2: a fila ganha travado_em (indexado, para contar os travados sem varrer a
// tabela) e ultimo_codigo. Sem migracao de dado: o IndexedDB nao indexa null,
// entao linha antiga fica fora do indice de travados -- que e o significado
// certo. Nada se perde.
banco.version(2).stores({
  outbox: 'id, criado_em, travado_em',
})

// v3: configuracao de metas (spec 2026-08-05 §8). Dexie so precisa das chaves
// que a gente CONSULTA; os outros campos vem junto no objeto.
//
// ⚠ ERA v2 nesta branch, e DESCEU para v3 no merge de 11/08. Motivo: o P2
// entrou na main antes e ja tomou a v2 -- e a v2 dele JA ESTA NO APARELHO dos
// usuarios. Duas definicoes diferentes do mesmo numero de versao fariam o Dexie
// abrir o banco existente com o schema errado. A ordem das versoes tambem
// importa: v3 tem de vir DEPOIS da v2 no arquivo.
banco.version(3).stores({
  configuracao_metas: 'id',
})

// v4: motor de imposto (0024/spec 2026-09-06) -- 3 tabelas novas, so leitura
// pelo app (gravadas por import server-side, nunca por gravarLocalEEnfileirar).
banco.version(4).stores({
  aliquotas_icms: 'id, representada_id, produto_id, [produto_id+uf_destino]',
  aliquotas_st: 'id, representada_id, produto_id, [produto_id+uf_destino]',
  descontos_frete: 'id, representada_id, [representada_id+regiao_destino+incoterm]',
})

// v5: compromissos avulsos (0030, spec 2026-10-02 ciclo 2A). Indice por data:
// o calendario e o card do Inicio leem por dia/mes.
banco.version(5).stores({
  compromissos: 'id, data, cliente_id',
})

// v6: diferenca do mes da fornecedora (0031, spec 2026-10-05).
banco.version(6).stores({
  diferencas_comissao: 'id, representada_id, mes',
})

// v7: dados da empresa do representante (0033, spec 2026-10-06).
banco.version(7).stores({
  empresas: 'id',
})

// v8: segmentos do vendedor (0037, pedido dela 11/10).
banco.version(8).stores({
  segmentos: 'id, nome',
})
